import styled from "styled-components";
import {formats} from "common/graphs/NVStackedAreaChart";

export const TileContainer = styled.div`
  position: relative;
  min-height: 6cm;
  display: flex;
  flex-direction: column;
  & > h4 {
    text-align: center;
    margin: 20px 0 0 0;
  }
  .graph {
    flex: 1 1 100%;
    margin: 0;
    .nv-y.nv-axis .nv-axisMaxMin {
      display: none;
    }

    .legends {
      ul {
        margin-top: 5px;
        margin-bottom: 0;
      }

      margin-right: 30px;
    }

    @media screen and (min-width: 1270px) {
      .legends {
        right: 0;

        ul {
          margin-top: 0;
          margin-bottom: 0;
        }
      }
    }
  }
  &.spread {
    justify-content: space-between;
    padding: 1em;
    align-content: flex-start;
  }
  &.can-compress {
    min-height: 0;
  }
  .input-group {
    max-width: 20ch;
  }
  &.title-and-graph .title {
    font-family: "AllianceNo2-SemiBold";
    font-weight: 100;
    text-align: left !important;
    color: var(--subtitle-color);
    font-size: var(--chart-title-font-size, 18px);
    text-transform: var(--chart-title-text-transform, capitalize);
    margin: 0;
  }
`;

export function getInRangeItems(items, range, fields, sampleSize) {
  if (items.length === 0 || fields.length === 0) {
    return [];
  }

  const { from, to } = range;
  if (sampleSize === 1) {
    return items.filter(item => from <= item.time && item.time <= to);
  }
  let inrangeItems = [];
  let maxItems;
  for (let i = 0; i < items.length; i += 1) {
    const { time } = items[i];
    if (from <= time && time <= to) {
      if (!maxItems) {
        maxItems = { ...items[i] };
      }
      if (i % sampleSize === 0) {
        inrangeItems.push(maxItems);
        maxItems = { ...items[i] };
      } else {
        const newMax = fields.reduce((acc, { name }) => {
          if (items[i][name] > acc[name]) {
            acc[name] = items[i][name];
          }
          return acc;
        }, maxItems);
        maxItems = newMax;
      }
    }
  }

  if (inrangeItems.length === 0) {
    return [];
  }

  if (
    inrangeItems[inrangeItems.length - 1]?.time &&
    items[items.length - 1]?.time &&
    inrangeItems[inrangeItems.length - 1].time !== items[items.length - 1]?.time
  ) {
    const { time } = items[items.length - 1];
    if (from <= time && time <= to) {
      inrangeItems.push(items[items.length - 1]);
    }
  }
  
  return inrangeItems;
}

export const timeCoeffMap = {
  24: 1000 * 60 * 5,
  168: 1000 * 60 * 10,
  720: 1000 * 60 * 60,
  744: 1000 * 60 * 60,
  2160: 1000 * 60 * 60,
  2232: 1000 * 60 * 60,
};

export const oneDecimalWithPercentage = (d) =>
  d === undefined || d === null ? undefined : `${formats.oneDecimal(d)}%`;

export const linesFromHours = (hours) => hours * (60 / 5);

const diffTime = 300000;

const fillGaps = (items, fieldNames, range) => {
  const { from, to } = range;
  const timeFrom = new Date(from);
  const itemsInRange = items.filter((item) => from <= item.time && item.time <= to);

  if (itemsInRange.length === 0 || !itemsInRange[0].time ) {
    return [];
  }

  const first = itemsInRange[0].time;
  const newTimeFrom = new Date(timeFrom.getTime() + diffTime);

  if (newTimeFrom < first) {
    const fieldsEntry = fieldNames.reduce((accEntry, field) => {
      accEntry[field] = null;
      return accEntry;
    }, {});
    const extendedItems = [...itemsInRange];
    while (newTimeFrom < extendedItems[0].time) {
      const start = extendedItems[0].time;
      const startTime = new Date(start);
      const newItem = {
        time: new Date(startTime.getTime() - diffTime),
        ...fieldsEntry
      };
      extendedItems.unshift(newItem);
    }
    return extendedItems;
  }
  return itemsInRange;
};

export const getAllItems = (allResults, range) => {
  const items = Object.values(allResults).map(item => item?.items);
  const maxItems = items.reduce((max, item) => Math.max(max, item?.length || 0), 0);
  if (maxItems === 0){
    return undefined;
  }
  return Object.entries(allResults).reduce((acc, [key, itemsResult])=> {
    const { items, fields } = itemsResult;
    if (items.length === 0 || fields.length === 0){
      return acc;
    }
    const processedItems = fillGaps(items, fields, range);
    if (processedItems.length > 0) {
      acc[key] = processedItems;
    }
    return acc;
  }, {});
}

export const getAllItemsNotAggregated = (result, fields, range) => {
  if (!range || !result || !fields || !result.items || fields.length === 0 || result.items.length === 0){
    return [];
  }
  const fieldNames = fields.map(field => field.name);
  return fillGaps(result.items, fieldNames, range);
};
